import classNames from "classnames";
import React, { ReactNode } from "react";
import { useListener, useUpdate } from "../react";
import { ViewController } from "./view-controller";
import { styles } from "./views.scss";


export interface View
{
    parent?: View;
    isVisible: boolean;
    on(event: "update", handler: (view: View) => void): this;
    off(event: "update", handler: (view: View) => void): this;
}


export function View(props: View.Props)
{
    return (
        (ControlledView.isProps(props))
            ? <ControlledView {...props} />
            : <NativeView {...props} />
    );
}


export namespace View
{
    export type Props =
        (
            {
                view?: View;
                visible?: boolean;
                children: ReactNode;
            } &
            (
                {
                    view: View;
                    visible?: never;
                } |
                {
                    visible: boolean;
                    view?: never;
                }
            )
        );

    export function create<T extends Record<string, View>>(innerViews?: T): View & T
    {
        return ViewController.create(innerViews);
    }
}


function ControlledView({ view, children }: ControlledView.Props)
{
    const update = useUpdate();
    useListener(view, "update", update);
    const { isVisible } = view;

    return (
        <div className={classNames(styles.view, isVisible && styles.visible)}>
            {children}
        </div>
    );
};

namespace ControlledView
{
    export interface Props
    {
        view: View;
        children: ReactNode;
    }

    export function isProps(value: any): value is Props;
    export function isProps(value: Props): value is Props
    {
        return typeof value == "object" && !!value.view;
    }
}


function NativeView({ visible, children }: NativeView.Props)
{
    return (
        <div className={classNames(styles.view, visible && styles.visible)}>
            {children}
        </div>
    );
};

namespace NativeView
{
    export interface Props
    {
        visible: boolean;
        children: ReactNode;
    }
}

